<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout wrap align-center>
        <v-flex xs12 sm4 md4 d-flex>
          <v-text-field
              v-model="filters.name"
              append-icon="search"
              :label="trans('app.search.search')"
              single-line
              hide-details
              solo
              clearable
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-autocomplete
              clearable
              item-text="name"
              item-value="id"
              :items="categories"
              :label="trans('app.pages.products.category')"
              v-model="category"
              solo
              hide-details
          >
            <template slot="item" slot-scope="data">
              <p :class="data.item.is_main && 'isMain'">{{ data.item.name }}</p>
            </template>
          </v-autocomplete>
        </v-flex>
        <v-flex xs12 sm4 md4 d-flex>
          <v-combobox
              multiple
              clearable
              :items="dictionaries.products.map(t => t.name)"
              :label="trans('app.tags.tags')"
              v-model="filters.tags.product"
              hide-details
              solo
          ></v-combobox>
        </v-flex>
        <v-flex xs12 sm8 md8 d-flex>
          <v-layout wrap align-center>
            <v-flex xs12 sm6 md6>
              <permission-tooltip
                  :permission="`product_list_nutrients_search`"
                  :tooltip="{top: true, bottom: false, attach: false}"
              >
                <v-autocomplete
                    :disabled="!hasPermission('product_list_nutrients_search')"
                    clearable
                    item-text="name"
                    :items="nutrients"
                    :label="trans('app.pages.products.nutrient')"
                    v-model="nutrient"
                    hide-details
                    solo
                    return-object
                    @change="nutrientChange()"
                ></v-autocomplete>
              </permission-tooltip>
            </v-flex>
            <v-flex xs12 sm3 md3>
              <permission-tooltip
                  :permission="`product_list_nutrients_search`"
                  :tooltip="{top: true, bottom: false, attach: false}"
              >
                <v-text-field
                    :disabled="!nutrient || !hasPermission('product_list_nutrients_search')"
                    v-model="nutrient_from"
                    :label="trans('app.search.from')"
                    single-line
                    hide-details
                    solo
                ></v-text-field>
              </permission-tooltip>
            </v-flex>
            <v-flex xs12 sm3 md3>
              <permission-tooltip
                  :permission="`product_list_nutrients_search`"
                  :tooltip="{top: true, bottom: false, attach: false}"
              >
                <v-text-field
                    :disabled="!nutrient || !hasPermission('product_list_nutrients_search')"
                    v-model="nutrient_to"
                    :label="trans('app.search.to')"
                    single-line
                    hide-details
                    solo
                ></v-text-field>
              </permission-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs12 sm1 md1 d-flex>
          <v-btn color="warning" @click="resetSearch">{{ 'app.reset' | trans }}</v-btn>
        </v-flex>
        <slot name="header_actions"></slot>
      </v-layout>
    </v-container>
    <v-data-table
        :disable-initial-sort="true"
        :headers="headers"
        :items="items"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
        :no-data-text="trans('app.search.no_data')"
    >
      <template slot="headerCell" slot-scope="props">
        <v-tooltip top v-if="props.header.tooltip">
            <span slot="activator">
              {{ props.header.text }}
            </span>
          <span>
              {{ props.header.tooltip }}
            </span>
        </v-tooltip>
        <span v-else>
          {{ props.header.text }}
        </span>
      </template>
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td>
            {{ props.item.name }}
            <template v-if="props.item.shared">
              <v-tooltip right>
                <template #activator="{ on }">
                  <v-icon v-on="on" small color="success">share</v-icon>
                </template>
                <span>{{ trans('app.sharing.shared_by', {name: props.item.owned_by}) }}</span>
              </v-tooltip>
            </template>
          </td>
          <td>
            {{ props.item.kcal }}
          </td>
          <td>
            {{ props.item.carbo }}
          </td>
          <td>
            {{ props.item.protein }}
          </td>
          <td>
            {{ props.item.fats }}
          </td>
          <td>
            {{ props.item.fiber }}
          </td>
          <td v-if="shouldDisplayAdditionalNutrientColumn()">
            {{ props.item.nutrient_value }}
          </td>
          <td class="justify-end layout px-0 pr-4">
            <v-layout align-center justify-end>
              <icon-button
                  icon="launch"
                  color="light-blue"
                  cls="mr-0 ml-0"
                  :permission="canView(props.item)"
                  @click="$emit('showProduct', {id: props.item.id})"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.open' | trans }}
              </icon-button>
              <icon-button
                  icon="edit"
                  :color="canBeEdited(props.item) ? 'success' : 'grey'"
                  cls="mr-0 ml-0"
                  @click="canBeEdited(props.item) ? $emit('editProduct', {id: props.item.id}) : () => {}"
                  :permission="canEdit(props.item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{
                  'app.edit' | trans
                }}
              </icon-button>
              <icon-button
                  icon="content_copy"
                  color="success"
                  cls="mr-0 ml-0"
                  :permission="canCopy(props.item)"
                  @click="canCopy(props.item) ? $emit('copyAndEditProduct', {id: props.item.id}) : () => {}"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.copy' | trans }}
              </icon-button>
              <icon-button
                  v-if="props.item.shared === false"
                  @click="favorite(props.item)"
                  :icon="props.item.favourites_user_id ? 'favorite' : 'favorite_border'"
                  color="amber"
                  cls="mr-0 ml-0"
                  :permission="`product_favorite`"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ props.item.favourites_user_id ? 'app.favorited' : 'app.add_to_favorites' | trans }}
              </icon-button>
              <icon-button
                  icon="delete"
                  :color="canBeDeleted(props.item) ? 'red' : 'grey'"
                  cls="mr-0 ml-0"
                  @click="canBeDeleted(props.item) ? deleteItem(props.item) : () => {}"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.delete' | trans }}
              </icon-button>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
    <div class="text-xs-center pt-2 pb-4">
      <v-pagination v-model="pagination.page" total-visible=10 :length="pages"></v-pagination>
    </div>
  </div>
</template>
<script>
import {stringify} from "qs";
import {trans} from "../../app";
import {mapGetters, mapState} from "vuex";
import debounce from "lodash/debounce";
import {helpers} from "../../helpers";

export default {
  props: {
    group: String,
  },
  data: () => ({
    filters: {
      name: '',
      tags: {
        product: null,
      },
    },
    seasons: [
      {
        name: trans('app.months.january'),
        value: '1',
      },
      {
        name: trans('app.months.february'),
        value: '2',
      },
      {
        name: trans('app.months.march'),
        value: '3',
      },
      {
        name: trans('app.months.april'),
        value: '4',
      },
      {
        name: trans('app.months.may'),
        value: '5',
      },
      {
        name: trans('app.months.june'),
        value: '6',
      },
      {
        name: trans('app.months.july'),
        value: '7',
      },
      {
        name: trans('app.months.august'),
        value: '8',
      },
      {
        name: trans('app.months.september'),
        value: '9',
      },
      {
        name: trans('app.months.october'),
        value: '10',
      },
      {
        name: trans('app.months.november'),
        value: '11',
      },
      {
        name: trans('app.months.december'),
        value: '12',
      },
    ],
    category: null,
    selectedSeasons: [],
    nutrient: null,
    nutrient_from: null,
    nutrient_to: null,
    pagination: {
      rowsPerPage: 50,
      sortBy: 'name',
      descending: false,
    },
    total: 0,
    items: [],
    isLoading: true,
    pages: 0,
  }),
  computed: {
    ...mapState(['dictionaries']),
    ...mapGetters(['hasPermission']),
    headers() {
      let headers = [
        {
          text: trans('app.name'), align: 'left', value: 'name', sortable: true,
        },
        {
          text: 'Kcal', align: 'left', value: 'kcal', sortable: true, tooltip: trans('app.nutrients.kcal'),
        },
        {
          text: 'W (g)', align: 'left', value: 'carbo', sortable: true, tooltip: trans('app.nutrients.carbo'),
        },
        {
          text: 'B (g)', align: 'left', value: 'protein', sortable: true, tooltip: trans('app.nutrients.protein'),
        },
        {
          text: 'T (g)', align: 'left', value: 'fats', sortable: true, tooltip: trans('app.nutrients.fats'),
        },
        {
          text: 'Bł (g)', align: 'left', value: 'fiber', sortable: true, tooltip: trans('app.nutrients.fiber'),
        },
        {
          text: '', align: 'center', sortable: false,
        },
      ];
      if (this.shouldDisplayAdditionalNutrientColumn()) {
        headers[6] = {
          text: this.nutrient.name + (this.nutrient.unit ? ' (' + this.nutrient.unit + ')' : ''),
          align: 'left',
          value: 'nutrient_value',
          sortable: true,
        };
        headers[7] = {
          text: '', align: 'center', sortable: false,
        };
      }
      return headers;
    },
    nutrients() {
      let nutrients = helpers.deepClone(this.dictionaries.nutrients);
      return nutrients.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
    },
    categories() {
      let categories = helpers.deepClone(this.dictionaries.product_categories)
          .sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
      return categories.reduce((prevValue, currValue) => {
        return [...prevValue, ...this.flatNestedCategories(currValue)];
      }, []);
    }
  },
  created() {
    this.debounceFetch = debounce(this.fetchData, 300);
  },
  watch: {
    selectedSeasons: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    filters: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    category: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    nutrient: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    nutrient_from: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    nutrient_to: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    pagination: {
      handler(newValue, oldValue) {
        if (oldValue.page !== undefined) {
          this.fetchData();
        }
      },
      deep: true,
    },
  },
  methods: {
    nutrientChange() {
      if (this.nutrient && this.nutrient_from === null && this.nutrient_to === null) {
        if ([1, 2, 3, 5, 20].includes(this.nutrient.id)) {
          switch (this.nutrient.id) {
            case 1:
              this.pagination.sortBy = 'protein';
              break;
            case 2:
              this.pagination.sortBy = 'fats';
              break;
            case 3:
              this.pagination.sortBy = 'carbo';
              break;
            case 5:
              this.pagination.sortBy = 'kcal';
              break;
            case 20:
              this.pagination.sortBy = 'fiber'
              break
          }
        } else {
          this.pagination.sortBy = 'nutrient_value';
        }
        this.pagination.descending = true;
      }
      if (!this.nutrient) {
        this.pagination.sortBy = 'name';
        this.pagination.descending = false;
        this.nutrient_from = null;
        this.nutrient_to = null;
      }
    },
    shouldDisplayAdditionalNutrientColumn() {
      return this.nutrient && ![1, 2, 3, 5, 20].includes(this.nutrient.id);
    },
    resetSearch() {
      this.selectedSeasons = [];
      this.category = null;
      this.nutrient = null;
      this.nutrient_from = null;
      this.nutrient_to = null;
      this.filters = {
        name: '',
        tags: {
          product: null,
        },
      };
      this.pagination.rowsPerPage = 50;
      this.pagination.sortBy = 'name';
      this.pagination.descending = false;
    },
    async fetchData() {
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;

      const query = {
        group: this.group,
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
        ...this.filters,
      };

      if (this.nutrient) {
        query.nutrient_id = this.nutrient.id;
        if (this.nutrient_from) {
          query.nutrient_from = this.nutrient_from;
        }
        if (this.nutrient_to) {
          query.nutrient_to = this.nutrient_to;
        }
      }

      if (this.category) {
        query.category_id = this.category;
      }

      if (this.selectedSeasons.length > 0) {
        query.selected_seasons = JSON.stringify(this.selectedSeasons);
      }

      const response = await axios.get(`/v2/products?${stringify(query, {skipNulls: true})}`);

      const {data, meta} = response.data;
      this.items = data;
      this.total = meta.total;
      this.pages = meta.last_page;
      this.isLoading = false;
    },
    async favorite(item) {
      if (this.group === 'favorite') {
        await axios.delete(`/products/favourites/${item.id}`);
        this.$toast(trans('app.removed_from_favorites'));
      } else {
        const response = await axios.patch(`/products/favourites/${item.id}`);
        item.favourites_user_id = response.data.user_id;
        this.$toast(item.favourites_user_id ? trans('app.added_to_favorites') : trans('app.removed_from_favorites'));
      }
      await this.fetchData();
    },
    async deleteItem(item) {
      const confirm = await this.$confirm(
          trans('app.confirm_product_removal', {name: item.name}), {
            title: trans('app.remove') + ' ' + trans('app.product').toLowerCase(),
          });

      if (confirm) {
        const url = `/v2/products/${item.id}`;
        await axios.delete(url);
        await this.fetchData();
        this.$toast(trans('app.actions.removed', {name: trans('app.product').toLowerCase()}));
      }
    },
    flatNestedCategories(nestedCategory, level = 1) {
      const finalCategories = [];
      if (level > 1) {
        const prefix = new Array(level * 3).fill('\xA0').join('');
        nestedCategory.name =`${prefix}${nestedCategory.name}`;
      }
      finalCategories.push(nestedCategory);
      if (nestedCategory.subcategories.length > 0) {
        nestedCategory.subcategories.forEach(subcategory => {
          finalCategories.push(...this.flatNestedCategories(subcategory, level + 1));
        })
      }

      return finalCategories;
    },
    canView(item) {
      return item.can_view;
    },
    canEdit(item) {
      if (item.is_system && this.hasPermission('product_system_edit')) {
        return true;
      }
      return item.can_copy;
    },
    canCopy(item) {
      if (item.is_system && this.hasPermission('product_system_edit')) {
        return true;
      }
      return item.can_copy;
    },
    canBeEdited(item) {
      return ((item.is_system || item.shared) === false) || this.hasPermission('product_system_edit');
    },
    canBeDeleted(item) {
      return (item.is_system && item.can_delete) || (!item.is_system && !item.shared);
    },
  }
}
</script>
<style scoped>
  .isMain {
    font-weight: bold;
  }

  ::v-deep .v-list__tile{
    height:32px;
  }

  ::v-deep .v-list__tile p{
    margin-bottom:0;
  }
</style>
